import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import { Event } from "lib/build/events";
import {
	Antecedent,
	deleteRecording,
	fetchAntecedents,
	fetchRecordings,
	Recording,
	saveRecording
} from "lib/build/recordings";
import { LogItem, validate, Value } from "lib/build/schema";

import { Button } from "src/common/components/Button";
import {
	Fieldset,
	Form,
	FormBody,
	FormFooter,
	ValidationMessage
} from "src/common/components/Form";
import {
	GridColumn,
	GridRow,
	SplitColumn,
	SplitRow
} from "src/common/components/Grid";
import { FullCardPreloader } from "src/common/components/Preloader";
import { getApiErrorData, isApiErrorWithCode } from "src/common/utils/api";
import { isTodayOrBefore } from "src/common/utils/dates";
import { saveFiles } from "src/common/utils/files";
import { useConfirm } from "src/features/confirm";
import { Default } from "src/features/defaults";
import { fetchDefaults } from "src/features/defaults/utils";
import { useRefetchEvents } from "src/features/events";
import { FormContextProvider } from "src/features/forms/context/FormContext";
import { IsLockedContext } from "src/features/forms/context/IsLockedContext";
import { useGetDefaultTimestamp } from "src/features/forms/hooks/useGetDefaultTimestamp";
import { MiniFormData } from "src/features/forms/types";
import { mapLogItemsToEventQueries } from "src/features/forms/utils/mapLogItemsToEventQueries";
import {
	useCreateGenericError,
	useCreateMessage
} from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { PatientLink } from "src/features/patient";
import { genericMessages } from "src/messages/generic";

import { Antecedents } from "./Antecedents";
import { Attachments } from "./Attachments";
import { Examinations } from "./Examinations";
import { FieldsetRenderer } from "./FieldsetRenderer";
import { FieldsetSkeleton } from "./FieldsetSkeleton";
import { Timestamp } from "./Timestamp";

const emptyValue: Value = {};
const emptyDeafults: Default[] = [];

const messages = defineMessages({
	unlock: {
		id: "miniform.unlock",
		defaultMessage: "Szerkesztés"
	},
	unlockWarning: {
		id: "miniform.unlock_warning",
		defaultMessage:
			"Korábban rögzített adat módosítására készül. Ezáltal visszamenőlegesen megváltoztatja annak értékét."
	},
	unlockCancel: {
		id: "miniform.unlock_cancel",
		defaultMessage: "Mégse"
	},
	unlockCreate: {
		id: "miniform.unlock_create",
		defaultMessage: "Új adat felvétele"
	},
	unlockEdit: {
		id: "miniform.unlock_edit",
		defaultMessage: "Meglévő módosítása"
	},
	deleteWarning: {
		id: "miniform.delete_warning",
		defaultMessage:
			"Korábban rögzített adat törlésére készül. Ezáltal visszamenőlegesen törli annak értékét."
	},
	deleteCancel: {
		id: "miniform.delete_cancel",
		defaultMessage: "Mégse"
	},
	deleteProceed: {
		id: "miniform.delete_proceed",
		defaultMessage: "Törlés"
	},
	saveSuccess: {
		id: "miniform.save_success",
		defaultMessage: "Az adatok rögzítése sikeresen megtörtént"
	},
	deleteSuccess: {
		id: "miniform.delete_success",
		defaultMessage: "Az adatok törlése sikeresen megtörtént"
	},
	existingTimestampError: {
		id: "miniform.exisitng_timestamp_error",
		defaultMessage:
			"Ezzel az időponttal a kiválasztott űrlap már kitöltésre került. Szeretné felülírni az előző mentést?"
	},
	versionMismatchError: {
		id: "miniform.version_mismatch_error",
		defaultMessage:
			"Ez a vizsgálati eredmény már módosítva lett egy másik felhasználó által. Szeretné felülírni az előző mentést?"
	}
});

interface MiniFormProps {
	patientId: string;
	form: MiniFormData;
}

enum UnlockSignal {
	Cancel = "CANCEL",
	Create = "CREATE",
	Edit = "EDIT"
}

enum DeleteSignal {
	Cancel = "CANCEL",
	Proceed = "PROCEED"
}

export const MiniForm: React.FunctionComponent<MiniFormProps> = React.memo(
	({ patientId, form }) => {
		const { org, mod } = useOrgModParams();
		const { formVersion: version = "" } = useParams<"formVersion">();

		const [state, dispatch] = React.useReducer(reducer, emptyState);

		const navigate = useNavigate();
		const confirm = useConfirm();
		const refetchEventsByQuery = useRefetchEvents(patientId);

		const getDefaultTimestamp = useGetDefaultTimestamp();
		const createGenericError = useCreateGenericError();
		const createMessage = useCreateMessage();

		const { formatMessage } = useIntl();

		const {
			id: formId,
			schemaId,
			showTimeInput = false,
			applyCurrentDate = false,
			applyDateOfBirth = false,
			showAttachments = true,
			antecedents: antecedentQueries,
			validator,
			initialValue = emptyValue,
			defaults = emptyDeafults,
			fieldset,
			returnTo = "dashboard"
		} = form;

		const {
			isFetching,
			isSaving,
			isDeleting,
			recordings,
			antecedentEvents,
			isLocked,
			isSubmitted,
			timestamp,
			antecedents,
			savedFiles,
			unsavedFiles,
			isInitialized,
			value,
			messages: logMessages,
			warnings: logWarnings,
			errors: logErrors
		} = state;

		const isBusy = React.useMemo(
			() => isFetching || isSaving || isDeleting,
			[isFetching, isSaving, isDeleting]
		);

		const isTimestampValid = React.useMemo(
			() => timestamp !== "" && isTodayOrBefore(timestamp),
			[timestamp]
		);

		const isFormValid = React.useMemo(
			() => logErrors.length === 0,
			[logErrors]
		);

		const isValid = React.useMemo(
			() => isTimestampValid && isFormValid,
			[isTimestampValid, isFormValid]
		);

		const isMountedRef = React.useRef(true);

		React.useEffect(() => {
			isMountedRef.current = true;
			return () => {
				isMountedRef.current = false;
			};
		}, []);

		const initializeWithoutVersion = React.useCallback(async () => {
			dispatch({ type: "RESET" });
			dispatch({ type: "FETCH_START" });
			try {
				const recordings = await fetchRecordings({
					org,
					mod,
					patientId,
					formId,
					schemaId
				});
				let antecedents: Event[];
				if (antecedentQueries === undefined) {
					antecedents = [];
				} else {
					antecedents = await fetchAntecedents({
						org,
						mod,
						patientId,
						queries: antecedentQueries,
						savedAntecedents: []
					});
				}
				const defaultValue = await fetchDefaults({
					org,
					mod,
					patientId,
					defaults
				});
				const result = await validate({
					validator,
					value: { ...initialValue, ...defaultValue },
					options: {
						api: {
							search: `organization=${org}&module=${mod}`
						}
					}
				});
				if (!isMountedRef.current) {
					return;
				}
				dispatch({
					type: "FETCH_SUCCESS",
					payload: { recordings, antecedents }
				});
				dispatch({
					type: "SET_TIMESTAMP",
					payload: {
						timestamp:
							getDefaultTimestamp({
								showTimeInput,
								applyCurrentDate,
								applyDateOfBirth
							}) || ""
					}
				});
				dispatch({
					type: "INITIALIZE_FORM_STATE",
					payload: {
						value: result.value,
						messages: result.messages || [],
						warnings: result.warnings || [],
						errors: result.errors || []
					}
				});
			} catch (error) {
				if (!isMountedRef.current) {
					return;
				}
				dispatch({
					type: "FETCH_FAILURE",
					payload: { error }
				});
				createGenericError();
			}
		}, [
			org,
			mod,
			patientId,
			formId,
			schemaId,
			showTimeInput,
			applyCurrentDate,
			applyDateOfBirth,
			antecedentQueries,
			validator,
			initialValue,
			defaults,
			getDefaultTimestamp,
			createGenericError
		]);

		const initializeWithVersion = React.useCallback(async () => {
			dispatch({ type: "RESET" });
			dispatch({ type: "FETCH_START" });
			try {
				const recordings = await fetchRecordings({
					org,
					mod,
					patientId,
					formId,
					schemaId
				});
				const recording = recordings.find(
					recording => recording.version === version
				);
				if (recording === undefined) {
					throw new Error(
						`recoring with version (${version}) not found`
					);
				}
				let antecedents: Event[];
				if (antecedentQueries === undefined) {
					antecedents = [];
				} else {
					antecedents = await fetchAntecedents({
						org,
						mod,
						patientId,
						queries: antecedentQueries,
						savedAntecedents: recording.antecedents || []
					});
				}
				const result = await validate({
					validator,
					value: recording.value,
					options: {
						api: {
							search: `organization=${org}&module=${mod}`
						}
					}
				});
				if (!isMountedRef.current) {
					return;
				}
				dispatch({
					type: "FETCH_SUCCESS",
					payload: { recordings, antecedents }
				});
				dispatch({
					type: "SET_LOCKED",
					payload: { isLocked: true }
				});
				dispatch({
					type: "SET_TIMESTAMP",
					payload: { timestamp: recording.timestamp }
				});
				dispatch({
					type: "SET_ANTECEDENTS",
					payload: {
						antecedents: recording.antecedents || []
					}
				});
				dispatch({
					type: "SET_SAVED_FILES",
					payload: { savedFiles: recording.attachments || [] }
				});
				dispatch({
					type: "INITIALIZE_FORM_STATE",
					payload: {
						value: result.value,
						messages: result.messages || [],
						warnings: result.warnings || [],
						errors: result.errors || []
					}
				});
			} catch (error) {
				if (!isMountedRef.current) {
					return;
				}
				dispatch({
					type: "FETCH_FAILURE",
					payload: { error }
				});
				createGenericError();
			}
		}, [
			org,
			mod,
			patientId,
			formId,
			schemaId,
			version,
			antecedentQueries,
			validator,
			createGenericError
		]);

		React.useEffect(() => {
			if (version === "") {
				initializeWithoutVersion();
			} else {
				initializeWithVersion();
			}
		}, [version, initializeWithoutVersion, initializeWithVersion]);

		const refetchEvents = React.useCallback(async () => {
			for (const query of mapLogItemsToEventQueries(logMessages)) {
				refetchEventsByQuery(query);
			}
		}, [logMessages, refetchEventsByQuery]);

		const navigateToReturnTo = React.useCallback(() => {
			navigate(`/${org}/${mod}/patients/${patientId}/${returnTo}`);
		}, [navigate, org, mod, patientId, returnTo]);

		const navigateToNew = React.useCallback(() => {
			navigate(`/${org}/${mod}/patients/${patientId}/form/${formId}`);
			initializeWithoutVersion();
		}, [navigate, org, mod, patientId, formId, initializeWithoutVersion]);

		const onChangeVersion = React.useCallback(
			(version: string | undefined) => {
				if (version === undefined) {
					navigate(
						`/${org}/${mod}/patients/${patientId}/form/${formId}`
					);
				} else {
					navigate(
						`/${org}/${mod}/patients/${patientId}/form/${formId}/${version}`
					);
				}
			},
			[navigate, org, mod, patientId, formId]
		);

		const onChangeTimestamp = React.useCallback(
			(value: string | undefined) => {
				dispatch({
					type: "SET_TIMESTAMP",
					payload: { timestamp: value || "" }
				});
			},
			[]
		);

		const onChangeAntecedents = React.useCallback(
			(antecedents: Antecedent[]) => {
				dispatch({
					type: "SET_ANTECEDENTS",
					payload: { antecedents }
				});
			},
			[]
		);

		const onChangeSavedFiles = React.useCallback((savedFiles: string[]) => {
			dispatch({
				type: "SET_SAVED_FILES",
				payload: { savedFiles }
			});
		}, []);

		const onChangeUnsavedFiles = React.useCallback(
			(unsavedFiles: File[]) => {
				dispatch({
					type: "SET_UNSAVED_FILES",
					payload: { unsavedFiles }
				});
			},
			[]
		);

		const valueRef = React.useRef(value);

		React.useEffect(() => {
			valueRef.current = value;
		}, [value]);

		const onChangeValue = React.useCallback(
			async (valueOrFn: Value | ((prevValue: Value) => Value)) => {
				const result = await validate({
					validator,
					value:
						typeof valueOrFn === "function"
							? valueOrFn(valueRef.current)
							: valueOrFn,
					options: {
						api: {
							search: `organization=${org}&module=${mod}`
						}
					}
				});
				if (!isMountedRef.current) {
					return;
				}
				dispatch({
					type: "SET_FORM_STATE",
					payload: {
						value: result.value,
						messages: result.messages || [],
						warnings: result.warnings || [],
						errors: result.errors || []
					}
				});
			},
			[org, mod, validator]
		);

		const onUnlock = React.useCallback(async () => {
			const signal = await confirm({
				content: formatMessage(messages.unlockWarning),
				negativeActions: [
					{
						text: formatMessage(messages.unlockCancel),
						value: UnlockSignal.Cancel
					}
				],
				positiveActions: [
					{
						text: formatMessage(messages.unlockCreate),
						value: UnlockSignal.Create
					},
					{
						text: formatMessage(messages.unlockEdit),
						value: UnlockSignal.Edit
					}
				]
			});
			if (!isMountedRef.current) {
				return;
			}
			if (signal === UnlockSignal.Create) {
				onChangeVersion(undefined);
			} else if (signal === UnlockSignal.Edit) {
				dispatch({
					type: "SET_LOCKED",
					payload: { isLocked: false }
				});
			}
		}, [confirm, formatMessage, onChangeVersion]);

		const onDelete = React.useCallback(async () => {
			if (timestamp === "") {
				return;
			}
			const signal = await confirm({
				content: formatMessage(messages.deleteWarning),
				negativeActions: [
					{
						text: formatMessage(messages.deleteCancel),
						value: DeleteSignal.Cancel
					}
				],
				positiveActions: [
					{
						text: formatMessage(messages.deleteProceed),
						value: DeleteSignal.Proceed
					}
				]
			});
			if (!isMountedRef.current) {
				return;
			}
			if (signal === DeleteSignal.Cancel) {
				return;
			}
			dispatch({ type: "DELETE_START" });
			try {
				await deleteRecording({
					org,
					mod,
					patientId,
					formId,
					schemaId,
					timestamp
				});
				if (!isMountedRef.current) {
					return;
				}
				dispatch({ type: "DELETE_SUCCESS" });
				createMessage(messages.deleteSuccess);
				refetchEvents();
				navigateToReturnTo();
			} catch (error) {
				if (!isMountedRef.current) {
					return;
				}
				dispatch({
					type: "DELETE_FAILURE",
					payload: { error }
				});
				createGenericError();
			}
		}, [
			org,
			mod,
			patientId,
			formId,
			schemaId,
			timestamp,
			confirm,
			createGenericError,
			createMessage,
			formatMessage,
			refetchEvents,
			navigateToReturnTo
		]);

		const onSaveInner = React.useCallback(
			async (recording: Recording): Promise<Recording | null> => {
				try {
					return await saveRecording({
						org,
						mod,
						patientId,
						formId,
						schemaId,
						recording
					});
				} catch (error) {
					if (
						!isApiErrorWithCode<string>(
							error,
							"recordings.save.failed.concurrent"
						)
					) {
						throw error;
					}
					const msg =
						recording.version === ""
							? messages.existingTimestampError
							: messages.versionMismatchError;
					if (
						(await confirm({ content: formatMessage(msg) })) ===
						true
					) {
						const { payload: version } = getApiErrorData(error);
						return await onSaveInner({ ...recording, version });
					}
				}
				return null;
			},
			[org, mod, patientId, formId, schemaId, confirm, formatMessage]
		);

		const onSave = React.useCallback(
			async (
				recording: Recording,
				files: File[],
				onSuccess: () => unknown
			) => {
				dispatch({ type: "SAVE_START" });
				try {
					let data = recording;
					if (files.length !== 0) {
						data = {
							...data,
							attachments: [
								...data.attachments,
								...(await saveFiles({
									org,
									mod,
									patientId,
									files
								}))
							]
						};
					}
					const savedRecording = await onSaveInner(data);
					if (!isMountedRef.current) {
						return;
					}
					if (savedRecording === null) {
						dispatch({ type: "SAVE_CANCEL" });
					} else {
						dispatch({
							type: "SAVE_SUCCESS",
							payload: { recording }
						});
						createMessage(messages.saveSuccess);
						refetchEvents();
						onSuccess();
					}
				} catch (error) {
					if (!isMountedRef.current) {
						return;
					}
					dispatch({
						type: "SAVE_FAILURE",
						payload: { error }
					});
					createGenericError();
				}
			},
			[
				org,
				mod,
				patientId,
				onSaveInner,
				createGenericError,
				createMessage,
				refetchEvents
			]
		);

		const onSaveAndLeave = React.useCallback(async () => {
			dispatch({
				type: "SET_SUBMITTED",
				payload: { isSubmitted: true }
			});
			if (!isValid) {
				return;
			}
			try {
				await onSave(
					{
						version,
						timestamp,
						value,
						attachments: savedFiles,
						antecedents
					},
					unsavedFiles,
					navigateToReturnTo
				);
			} catch {
				createGenericError();
			}
		}, [
			isValid,
			version,
			timestamp,
			value,
			antecedents,
			savedFiles,
			unsavedFiles,
			onSave,
			navigateToReturnTo,
			createGenericError
		]);

		const onSaveAndStay = React.useCallback(async () => {
			dispatch({
				type: "SET_SUBMITTED",
				payload: { isSubmitted: true }
			});
			if (!isValid) {
				return;
			}
			try {
				await onSave(
					{
						version,
						timestamp,
						value,
						attachments: savedFiles,
						antecedents
					},
					unsavedFiles,
					navigateToNew
				);
			} catch {
				createGenericError();
			}
		}, [
			isValid,
			version,
			timestamp,
			value,
			antecedents,
			savedFiles,
			unsavedFiles,
			onSave,
			navigateToNew,
			createGenericError
		]);

		return (
			<Form isSubmitted={isSubmitted} onSubmit={onSaveAndLeave}>
				<FormBody>
					<Fieldset>
						<GridRow>
							<GridColumn columnSize={6}>
								<Timestamp
									showTimeInput={showTimeInput}
									disabled={isBusy || version !== ""}
									timestamp={timestamp || undefined}
									onChangeTimestamp={onChangeTimestamp}
								/>
							</GridColumn>
							<GridColumn columnSize={6}>
								<Examinations
									recordings={recordings}
									version={version}
									onChangeVersion={onChangeVersion}
								/>
							</GridColumn>
						</GridRow>
						{antecedentQueries !== undefined &&
							antecedentQueries.length > 0 && (
								<GridRow>
									<GridColumn columnSize={12}>
										<Antecedents
											disabled={isBusy || isLocked}
											events={antecedentEvents}
											antecedents={antecedents}
											onChangeAntecedents={
												onChangeAntecedents
											}
										/>
									</GridColumn>
								</GridRow>
							)}
					</Fieldset>
					<Fieldset>
						{isFetching || !isInitialized ? (
							<FieldsetSkeleton />
						) : isSaving || isDeleting ? (
							<FullCardPreloader />
						) : (
							<IsLockedContext.Provider value={isLocked}>
								<FormContextProvider
									isInitialized={isInitialized}
									value={value}
									setValue={onChangeValue}
									messages={logMessages}
									warnings={logWarnings}
									errors={logErrors}
								>
									<FieldsetRenderer
										{...fieldset}
										key={version}
									/>
								</FormContextProvider>
							</IsLockedContext.Provider>
						)}
					</Fieldset>
					{showAttachments && (
						<Fieldset>
							<Attachments
								disabled={isBusy || isLocked}
								savedFiles={savedFiles}
								onChangeSavedFiles={onChangeSavedFiles}
								unsavedFiles={unsavedFiles}
								onChangeUnsavedFiles={onChangeUnsavedFiles}
							/>
						</Fieldset>
					)}
				</FormBody>
				<FormFooter>
					<SplitRow>
						<SplitColumn side="left">
							<PatientLink
								to="dashboard"
								className="button button--secondary"
							>
								<FormattedMessage {...genericMessages.cancel} />
							</PatientLink>
							{version !== "" && (
								<Button
									type="button"
									variant="danger"
									disabled={isBusy}
									onClick={onDelete}
								>
									<FormattedMessage
										{...genericMessages.delete}
									/>
								</Button>
							)}
						</SplitColumn>
						<SplitColumn side="right">
							{isSubmitted && !isValid && (
								<ValidationMessage>
									<FormattedMessage
										{...genericMessages.fixValidationErrors}
									/>
								</ValidationMessage>
							)}
							{isLocked ? (
								<Button
									type="button"
									disabled={isBusy}
									onClick={onUnlock}
								>
									<FormattedMessage {...messages.unlock} />
								</Button>
							) : (
								<>
									<Button
										type="button"
										variant="secondary"
										disabled={isBusy}
										onClick={onSaveAndStay}
									>
										<FormattedMessage
											{...genericMessages.saveAndNew}
										/>
									</Button>
									<Button
										type="submit"
										disabled={isBusy}
										onClick={onSaveAndLeave}
									>
										<FormattedMessage
											{...genericMessages.save}
										/>
									</Button>
								</>
							)}
						</SplitColumn>
					</SplitRow>
				</FormFooter>
			</Form>
		);
	}
);

interface State {
	isFetching: boolean;
	isSaving: boolean;
	isDeleting: boolean;
	recordings: Recording[];
	antecedentEvents: Event[];
	isLocked: boolean;
	isSubmitted: boolean;
	timestamp: string;
	antecedents: Antecedent[];
	savedFiles: string[];
	unsavedFiles: File[];
	isInitialized: boolean;
	value: Value;
	messages: LogItem[];
	warnings: LogItem[];
	errors: LogItem[];
}

const emptyState: State = {
	isFetching: false,
	isSaving: false,
	isDeleting: false,
	recordings: [],
	antecedentEvents: [],
	isLocked: false,
	isSubmitted: false,
	timestamp: "",
	antecedents: [],
	savedFiles: [],
	unsavedFiles: [],
	isInitialized: false,
	value: {},
	messages: [],
	warnings: [],
	errors: []
};

type Action =
	| ResetAction
	| FetchStartAction
	| FetchCancelAction
	| FetchSuccessAction
	| FetchFailureAction
	| SaveStartAction
	| SaveCancelAction
	| SaveSuccessAction
	| SaveFailureAction
	| DeleteStartAction
	| DeleteCancelAction
	| DeleteSuccessAction
	| DeleteFailureAction
	| SetSubmittedAction
	| SetLockedAction
	| SetTimestampAction
	| SetAntecedentsAction
	| SetSavedFilesAction
	| SetUnsavedFilesAction
	| InitializeFormStateAction
	| SetFormStateAction;

interface ResetAction {
	type: "RESET";
}

interface FetchStartAction {
	type: "FETCH_START";
}

interface FetchCancelAction {
	type: "FETCH_CANCEL";
}

interface FetchSuccessAction {
	type: "FETCH_SUCCESS";
	payload: { recordings: Recording[]; antecedents: Event[] };
}

interface FetchFailureAction {
	type: "FETCH_FAILURE";
	payload: { error: unknown };
}

interface SaveStartAction {
	type: "SAVE_START";
}

interface SaveCancelAction {
	type: "SAVE_CANCEL";
}

interface SaveSuccessAction {
	type: "SAVE_SUCCESS";
	payload: { recording: Recording };
}

interface SaveFailureAction {
	type: "SAVE_FAILURE";
	payload: { error: unknown };
}

interface DeleteStartAction {
	type: "DELETE_START";
}

interface DeleteCancelAction {
	type: "DELETE_CANCEL";
}

interface DeleteSuccessAction {
	type: "DELETE_SUCCESS";
}

interface DeleteFailureAction {
	type: "DELETE_FAILURE";
	payload: { error: unknown };
}

interface SetLockedAction {
	type: "SET_LOCKED";
	payload: { isLocked: boolean };
}

interface SetSubmittedAction {
	type: "SET_SUBMITTED";
	payload: { isSubmitted: boolean };
}

interface SetTimestampAction {
	type: "SET_TIMESTAMP";
	payload: { timestamp: string };
}

interface SetAntecedentsAction {
	type: "SET_ANTECEDENTS";
	payload: { antecedents: Antecedent[] };
}

interface SetSavedFilesAction {
	type: "SET_SAVED_FILES";
	payload: { savedFiles: string[] };
}

interface SetUnsavedFilesAction {
	type: "SET_UNSAVED_FILES";
	payload: { unsavedFiles: File[] };
}

interface InitializeFormStateAction {
	type: "INITIALIZE_FORM_STATE";
	payload: {
		value: Value;
		messages: LogItem[];
		warnings: LogItem[];
		errors: LogItem[];
	};
}

interface SetFormStateAction {
	type: "SET_FORM_STATE";
	payload: {
		value: Value;
		messages: LogItem[];
		warnings: LogItem[];
		errors: LogItem[];
	};
}

function reducer(state: State, action: Action): State {
	if (action.type === "RESET") {
		return emptyState;
	}
	if (action.type === "FETCH_START") {
		return handleFetchStart(state);
	}
	if (action.type === "FETCH_CANCEL") {
		return handleFetchCancel(state);
	}
	if (action.type === "FETCH_SUCCESS") {
		return handleFetchSuccess(state, action);
	}
	if (action.type === "FETCH_FAILURE") {
		return handleFetchFailure(state);
	}
	if (action.type === "SAVE_START") {
		return handleSaveStart(state);
	}
	if (action.type === "SAVE_CANCEL") {
		return handleSaveCancel(state);
	}
	if (action.type === "SAVE_SUCCESS") {
		return handleSaveSuccess(state, action);
	}
	if (action.type === "SAVE_FAILURE") {
		return handleSaveFailure(state);
	}
	if (action.type === "DELETE_START") {
		return handleDeleteStart(state);
	}
	if (action.type === "DELETE_CANCEL") {
		return handleDeleteCancel(state);
	}
	if (action.type === "DELETE_SUCCESS") {
		return handleDeleteSuccess(state);
	}
	if (action.type === "DELETE_FAILURE") {
		return handleDeleteFailure(state);
	}
	if (action.type === "SET_LOCKED") {
		return handleSetLockedAction(state, action);
	}
	if (action.type === "SET_SUBMITTED") {
		return handleSetSubmittedAction(state, action);
	}
	if (action.type === "SET_TIMESTAMP") {
		return handleSetTimestampAction(state, action);
	}
	if (action.type === "SET_ANTECEDENTS") {
		return handleSetAntecedents(state, action);
	}
	if (action.type === "SET_SAVED_FILES") {
		return handleSetSavedFiles(state, action);
	}
	if (action.type === "SET_UNSAVED_FILES") {
		return handleSetUnsavedFiles(state, action);
	}
	if (action.type === "INITIALIZE_FORM_STATE") {
		return handleInitializeFormStateAction(state, action);
	}
	if (action.type === "SET_FORM_STATE") {
		return handleSetFormStateAction(state, action);
	}
	return state;
}

function handleFetchStart(state: State): State {
	return { ...state, isFetching: true };
}

function handleFetchCancel(state: State): State {
	return { ...state, isFetching: false };
}

function handleFetchSuccess(state: State, action: FetchSuccessAction): State {
	const { recordings, antecedents } = action.payload;
	return {
		...state,
		isFetching: false,
		recordings,
		antecedentEvents: antecedents
	};
}

function handleFetchFailure(state: State): State {
	return { ...state, isFetching: false };
}

function handleSaveStart(state: State): State {
	return { ...state, isSaving: true };
}

function handleSaveCancel(state: State): State {
	return { ...state, isSaving: false };
}

function handleSaveSuccess(state: State, action: SaveSuccessAction): State {
	return {
		...state,
		isSaving: false,
		recordings: state.recordings.map(recording =>
			recording.timestamp === state.timestamp
				? action.payload.recording
				: recording
		)
	};
}

function handleSaveFailure(state: State): State {
	return { ...state, isSaving: false };
}

function handleDeleteStart(state: State): State {
	return { ...state, isDeleting: true };
}

function handleDeleteCancel(state: State): State {
	return { ...state, isDeleting: false };
}

function handleDeleteSuccess(state: State): State {
	return {
		...state,
		isDeleting: false,
		recordings: state.recordings.filter(
			recording => recording.timestamp !== state.timestamp
		)
	};
}

function handleDeleteFailure(state: State): State {
	return { ...state, isDeleting: false };
}

function handleSetLockedAction(state: State, action: SetLockedAction): State {
	return { ...state, ...action.payload };
}

function handleSetSubmittedAction(
	state: State,
	action: SetSubmittedAction
): State {
	return { ...state, ...action.payload };
}

function handleSetTimestampAction(
	state: State,
	action: SetTimestampAction
): State {
	return { ...state, ...action.payload };
}

function handleSetAntecedents(
	state: State,
	action: SetAntecedentsAction
): State {
	return { ...state, ...action.payload };
}

function handleSetSavedFiles(state: State, action: SetSavedFilesAction): State {
	return { ...state, ...action.payload };
}

function handleSetUnsavedFiles(
	state: State,
	action: SetUnsavedFilesAction
): State {
	return { ...state, ...action.payload };
}

function handleInitializeFormStateAction(
	state: State,
	action: InitializeFormStateAction
): State {
	return { ...state, isInitialized: true, ...action.payload };
}

function handleSetFormStateAction(
	state: State,
	action: SetFormStateAction
): State {
	return { ...state, ...action.payload };
}
